import { MAX_SECTIONS } from "./helpers";

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

export interface ImportedSection {
  title: string;
  purpose: string;
  prompt: string;
  sectionType?: "snapshot" | "docList";
  config?: {
    excludedCategoryNames?: string[];
  };
}

export interface ImportedTemplate {
  name: string;
  definition: string;
  sections: ImportedSection[];
}

export type ParseResult =
  | { success: true; data: ImportedTemplate }
  | {
      success: false;
      error: "FILE_TOO_LARGE" | "INVALID_JSON" | "INVALID_STRUCTURE";
      message: string;
    };

export function isImportedTemplate(data: unknown): data is ImportedTemplate {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const template = data as Record<string, unknown>;

  if (typeof template.name !== "string") {
    return false;
  }

  if (typeof template.definition !== "string") {
    return false;
  }

  if (!Array.isArray(template.sections)) {
    return false;
  }

  return template.sections.every((section) => {
    if (typeof section !== "object" || section === null) {
      return false;
    }
    const s = section as Record<string, unknown>;

    // Validate config if present
    if (s.config !== undefined) {
      if (typeof s.config !== "object" || s.config === null) {
        return false;
      }
      const config = s.config as Record<string, unknown>;
      if (
        config.excludedCategoryNames !== undefined &&
        (!Array.isArray(config.excludedCategoryNames) ||
          !config.excludedCategoryNames.every(
            (name) => typeof name === "string"
          ))
      ) {
        return false;
      }
    }

    return (
      typeof s.title === "string" &&
      typeof s.purpose === "string" &&
      typeof s.prompt === "string" &&
      (s.sectionType === undefined ||
        s.sectionType === "snapshot" ||
        s.sectionType === "docList")
    );
  });
}

export function parseImportedTemplate(
  text: string,
  fileSizeBytes?: number
): ParseResult {
  if (fileSizeBytes && fileSizeBytes > MAX_FILE_SIZE_BYTES) {
    return {
      success: false,
      error: "FILE_TOO_LARGE",
      message: `File size exceeds the maximum limit of 25MB`,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      success: false,
      error: "INVALID_JSON",
      message: "Invalid JSON format",
    };
  }

  if (!isImportedTemplate(parsed)) {
    return {
      success: false,
      error: "INVALID_STRUCTURE",
      message: "Template structure is invalid",
    };
  }

  return {
    success: true,
    data: {
      ...parsed,
      sections: parsed.sections.slice(0, MAX_SECTIONS),
    },
  };
}

interface TemplateExportData {
  name: string | null | undefined;
  definition: string | null | undefined;
  sections:
    | Array<{
        title: unknown;
        purpose: unknown;
        prompt: unknown;
        contextType?: unknown;
        config?: unknown;
      }>
    | null
    | undefined;
}

export function prepareTemplateExport(
  data: TemplateExportData
): ImportedTemplate {
  return {
    name: typeof data.name === "string" ? data.name : "",
    definition: typeof data.definition === "string" ? data.definition : "",
    sections: (data.sections ?? []).map((s) => {
      const section: ImportedSection = {
        title: typeof s.title === "string" ? s.title : "",
        purpose: typeof s.purpose === "string" ? s.purpose : "",
        prompt: typeof s.prompt === "string" ? s.prompt : "",
        sectionType:
          s.contextType === "docList"
            ? "docList"
            : s.contextType === "snapshot"
              ? "snapshot"
              : undefined,
      };

      // Handle config if present
      if (s.config && typeof s.config === "object" && s.config !== null) {
        const configObj = s.config as Record<string, unknown>;
        if (
          configObj.excludedCategoryNames &&
          Array.isArray(configObj.excludedCategoryNames)
        ) {
          // Convert to strings if needed (for API compatibility)
          const excludedNames = configObj.excludedCategoryNames
            .map((name) => String(name))
            .filter(
              (name) =>
                typeof name === "string" &&
                name.length > 0 &&
                name !== "null" &&
                name !== "undefined"
            );

          if (excludedNames.length > 0) {
            section.config = { excludedCategoryNames: excludedNames };
          }
        }
      }

      return section;
    }),
  };
}
